import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus, Clock, MessageSquareText } from 'lucide-react';
import { cancelExamDateRequest, fetchExamDateRequest, submitExamDateRequest } from '../api/candidate';
import {
  EXAM_DATE_NOTE_MAX,
  dateBounds,
  draftPayload,
  draftProblem,
  emptyExamDateDraft,
  hoursLabel,
  missingHours,
  periodLabel,
  type ExamDateDraft,
} from '../lib/examDateRequest';
import { Badge, Button, Callout, ConfirmDialog, Drawer, ProgressBar, TextAreaField, TextField } from './charte';

// Demande de date d'examen à l'auto-école (18/10/2026), sous « Mon examen » quand aucun examen n'est
// programmé : l'élève propose une période et ses disponibilités ; le bureau répond depuis le logiciel.
// Proposée dès les heures de conduite fixées par l'auto-école (J27, 15 h par défaut) ; avant, le panneau
// dit combien il en manque.

const QUERY_KEY = ['candidate', 'exam-date-request'];

const errorText = (e: unknown, fallback: string) =>
  e && typeof e === 'object' && 'message' in e && (e as { message?: unknown }).message
    ? String((e as { message?: unknown }).message)
    : fallback;

export default function ExamDateRequestPanel() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QUERY_KEY, queryFn: fetchExamDateRequest, retry: false });
  const [open, setOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [draft, setDraft] = useState<ExamDateDraft>(emptyExamDateDraft);
  const [touched, setTouched] = useState(false);

  const done = (data: Awaited<ReturnType<typeof fetchExamDateRequest>>) => {
    if (data) queryClient.setQueryData(QUERY_KEY, data);
    else void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };
  const submit = useMutation({
    mutationFn: () => submitExamDateRequest(draftPayload(draft)),
    onSuccess: (data) => {
      done(data);
      setOpen(false);
      setDraft(emptyExamDateDraft());
      setTouched(false);
    },
  });
  const cancel = useMutation({
    mutationFn: cancelExamDateRequest,
    onSuccess: (data) => {
      done(data);
      setConfirmCancel(false);
    },
  });

  const view = query.data;
  // Lecture impossible : rien n'est proposé plutôt qu'un bouton qui échouerait.
  if (!view) return null;
  const request = view.request;
  const missing = missingHours(view);
  const problem = draftProblem(draft);
  const bounds = dateBounds();

  return (
    <div className="mt-4 border-t border-slate-100 pt-4">
      {request?.status === 'pending' ? (
        <div className="grid gap-2" aria-label="Demande de date d’examen">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-heading">Demande de date envoyée</p>
            <Badge tone="todo">
              <Clock size={12} aria-hidden />
              En attente
            </Badge>
          </div>
          <p className="text-xs text-secondary">
            {periodLabel(request)} · votre auto-école vous répondra ici.
          </p>
          {request.note && <p className="whitespace-pre-line text-xs text-body">« {request.note} »</p>}
          <Button variant="ghost" size="sm" className="justify-self-start" onClick={() => setConfirmCancel(true)}>
            Retirer ma demande
          </Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {request && (
            <Callout tone={request.status === 'accepted' ? 'info' : 'note'}>
              <p className="font-semibold text-heading">
                {request.status === 'accepted' ? 'Votre auto-école a pris en compte votre demande.' : 'Votre auto-école a répondu à votre demande.'}
              </p>
              {request.reply && (
                <p className="mt-1 flex gap-1.5 whitespace-pre-line">
                  <MessageSquareText size={14} className="mt-0.5 shrink-0" aria-hidden />
                  <span>{request.reply}</span>
                </p>
              )}
            </Callout>
          )}
          {view.canRequest && (
            <Button variant="soft" size="sm" icon={CalendarPlus} className="justify-self-start" onClick={() => setOpen(true)}>
              {request ? 'Faire une nouvelle demande' : 'Demander une date d’examen'}
            </Button>
          )}
          {!view.canRequest && missing !== null && view.minHours !== null && view.drivingHours !== null && (
            <div className="grid gap-2" role="group" aria-label="Heures de conduite avant la demande d’examen">
              <p className="text-sm text-secondary">
                Vous pourrez demander une date d’examen dès {hoursLabel(view.minHours)} de conduite.
              </p>
              <ProgressBar value={(view.drivingHours / view.minHours) * 100} label="Heures de conduite faites" />
              <p className="text-xs text-secondary">
                {hoursLabel(view.drivingHours)} faites · encore {hoursLabel(missing)}
              </p>
            </div>
          )}
        </div>
      )}

      <Drawer
        open={open}
        onClose={() => !submit.isPending && setOpen(false)}
        title="Demander une date d’examen"
        subtitle="Votre auto-école reçoit votre demande et vous répond ici."
        footer={
          <>
            <Button variant="primary" loading={submit.isPending} disabled={!!problem} onClick={() => submit.mutate()}>
              Envoyer ma demande
            </Button>
            <Button variant="ghost" disabled={submit.isPending} onClick={() => setOpen(false)}>
              Annuler
            </Button>
          </>
        }
      >
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!problem) submit.mutate();
          }}
        >
          <p className="text-sm text-secondary">
            Indiquez la période qui vous conviendrait. Laissez les dates vides pour passer l’examen dès que possible : votre auto-école choisira avec
            vous selon les places disponibles.
          </p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              label="À partir du"
              type="date"
              min={bounds.min}
              max={draft.latestDate || bounds.max}
              value={draft.earliestDate}
              onChange={(e) => {
                setTouched(true);
                setDraft({ ...draft, earliestDate: e.target.value });
              }}
            />
            <TextField
              label="Au plus tard le"
              type="date"
              min={draft.earliestDate || bounds.min}
              max={bounds.max}
              value={draft.latestDate}
              onChange={(e) => {
                setTouched(true);
                setDraft({ ...draft, latestDate: e.target.value });
              }}
            />
          </div>
          <TextAreaField
            label="Message à votre auto-école (facultatif)"
            hint={`Disponibilités, contraintes… ${draft.note.trim().length}/${EXAM_DATE_NOTE_MAX}`}
            maxLength={EXAM_DATE_NOTE_MAX + 50}
            value={draft.note}
            onChange={(e) => {
              setTouched(true);
              setDraft({ ...draft, note: e.target.value });
            }}
          />
          {touched && problem && (
            <p role="alert" className="text-xs text-danger">
              {problem}
            </p>
          )}
          {submit.isError && (
            <p role="alert" className="text-xs text-danger">
              {errorText(submit.error, 'Votre demande n’a pas pu être envoyée. Réessayez.')}
            </p>
          )}
        </form>
      </Drawer>

      <ConfirmDialog
        open={confirmCancel}
        title="Retirer votre demande ?"
        confirmLabel="Retirer"
        pending={cancel.isPending}
        error={cancel.isError ? errorText(cancel.error, 'La demande n’a pas pu être retirée.') : undefined}
        onConfirm={() => cancel.mutate()}
        onCancel={() => setConfirmCancel(false)}
      >
        Votre auto-école ne la verra plus en attente. Vous pourrez en faire une nouvelle.
      </ConfirmDialog>
    </div>
  );
}
